"use client";

import React from "react";

import { Check, Info, TriangleAlert, X } from "lucide-react";
import { toast as sonnerToast, Toaster } from "sonner";

import { Button } from "@/components/ui/button";

/* 사용 예시:
<Button
  variant="outline"
  onClick={() => {
    toast({
      type: 'success',
      title: '성공',
      description: '작업이 성공적으로 완료되었습니다.',
    });
  }}
>
  성공 토스트 출력
</Button>
*/

/** 타입별 색상 매핑 */
const TOAST_COLORS = {
  default: { backgroundColor: "#fff", borderColor: "#999" },
  success: { backgroundColor: "#ECFDF5", borderColor: "#10B981" },
  error: { backgroundColor: "#FEF2F2", borderColor: "#EF4444" },
  info: { backgroundColor: "#EFF6FF", borderColor: "#3B82F6" },
  warning: { backgroundColor: "#FEFCE8", borderColor: "#FACC15" },
};

const TOAST_ICONS = {
  success: { icon: Check, className: "bg-success-600" },
  error: { icon: X, className: "bg-error-600" },
  info: { icon: Info, className: "bg-info-600" },
  warning: { icon: TriangleAlert, className: "bg-warning-600" },
};

/** 커스텀 toast 호출 함수 */
export function toast(toast: Omit<CustomToastProps, "id"> & { type: keyof typeof TOAST_COLORS }) {
  const type = toast.type ?? "default";

  return sonnerToast.custom(id => (
    <Toast id={id} title={toast.title} description={toast.description} type={type} />
  ));
}

/** 커스텀 Toast 컴포넌트 */
function Toast(props: CustomToastProps) {
  const { title, description, id, type } = props;
  const colorClass = TOAST_COLORS[type];

  return (
    <div
      style={{
        backgroundColor: colorClass.backgroundColor,
        borderColor: colorClass.borderColor,
      }}
      className="flex w-[340px] items-center justify-between rounded-lg border p-6"
    >
      <div className="flex flex-1 items-center gap-3 text-neutral-800">
        {type !== "default" && TOAST_ICONS[type] && (
          <span
            className={`${TOAST_ICONS[type].className} inline-flex size-6 shrink-0 items-center justify-center rounded-xl text-white [&>svg]:size-4`}
          >
            {React.createElement(TOAST_ICONS[type].icon)}
          </span>
        )}
        <div className="text-sm">
          <p className="sr-only font-semibold">{title}</p>
          <p className="whitespace-pre-line">{description}</p>
        </div>
      </div>

      {/* 닫기 버튼 */}
      <Button
        size="xs"
        variant="ghost"
        aria-label="토스트 팝업 닫기"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <X />
      </Button>
    </div>
  );
}

export function AppToast() {
  return <Toaster richColors position="bottom-right" />;
}

interface CustomToastProps {
  id: string | number;
  title: string;
  description: string;
  type: keyof typeof TOAST_COLORS;
  button?: {
    label: string;
    onClick: () => void;
  };
}
